import { useCallback, useEffect, useRef, useState } from "react";
import QuickSelectBar from "@/components/QuickSelectBar";
import SupplyList from "@/components/supplyList";
import Tabbar from "../Tabbar";
import useSupplyList from "@/hooks/useSupplyList";
import useTabbar from "@/hooks/useTabbar";
import { usePageState } from "@/context/PageStateContext";
import { CouponSelection, QuickSelection, Tab } from "@/types/tab";
import { useAppEnv } from "@/hooks/useAppEnv";
import { LX_CONFIG } from "@/track/waimai";
import { COUPON_SELECTION_ICON, DEFAULT_BIZ_SCENE, SEARCH_RULE_SCENE } from "../../constants";
import './index.scss';

const SupplyArea: React.FC = () => {
  // 使用 useAppEnv Hook 兼容 SSR/CSR 环境检测
  const appEnv = useAppEnv();

  const {
    locationInfo,
    couponRecallInfo, // 点击红包墙去使用按钮，更新展示的券快筛
    isOpenCity,
  } = usePageState();
  
  // 导航栏 + 快筛
  const {
    isInitFinished: isTabbarInitFinished,
    tabList,
    activeTab,
    activeQuickSelection,
    activeCouponSelection,
    couponSelectionList,
    quickSelectionList,
    useCouponSelection,
    getTabListData,
    handleTabClick,
    handleQuickSelectionClick,
    handleCouponSelectionClick,
    handleCouponGoToUseClick,
  } = useTabbar({
    searchRuleScene: SEARCH_RULE_SCENE,
    defaultBizScene: DEFAULT_BIZ_SCENE,
    enableQuickSelectionLazyLoad: true,
  });

  // 供给列表
  const {
    supplyList,
    isInitFinished: isSupplyListInitFinished,
    isLastPage,
    isLoading,
    isRequestError,
    loadMoreData,
    getSupplyListData,
    initSupplyList,
  } = useSupplyList({
    activeTab,
    activeQuickSelection,
    activeCouponSelection,
    defaultBizScene: DEFAULT_BIZ_SCENE,
  });
  const [isTabbarSticky, setIsTabbarSticky] = useState(false);
  const stickyDetectorRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const isSkipInitRef = useRef(false);

  // 切换 Tab、快筛时，Tabbar 吸顶
  const handleTabSticky = useCallback(() => {
    if (!stickyDetectorRef.current || !tabBarRef.current) return;
    const stickyDetectorRect = stickyDetectorRef.current.getBoundingClientRect();
    window.scrollBy({
      top: stickyDetectorRect.top + 2,
      behavior: 'smooth'
    });
  }, []);

  const handleTabClickSticky = useCallback((tab: Tab) => {
    handleTabClick(tab);
    handleTabSticky();
  }, [handleTabClick, handleTabSticky]);

  const handleQuickSelectionClickSticky = useCallback((selection: QuickSelection) => {
    handleQuickSelectionClick(selection);
    handleTabSticky();
  }, [handleQuickSelectionClick, handleTabSticky]);

  const handleCouponSelectionClickSticky = useCallback((selection: CouponSelection) => {
    handleCouponSelectionClick(selection);
    handleTabSticky();
  }, [handleCouponSelectionClick, handleTabSticky]);

  // 请求金刚区数据：初始化
  useEffect(() => {
    if (!locationInfo?.isInited) {
      return;
    }
    // 页面未对当前城市开放，不请求接口
    if (!isOpenCity) {
      return;
    }
    getTabListData();
  }, [locationInfo?.isInited]);

  // 请求供给列表数据：初始化
  useEffect(() => {
    // 检查是否初始化完成
    if (!locationInfo?.isInited) {
      return;
    }
    // 页面未对当前城市开放，不请求接口
    if (!isOpenCity) {
      return;
    }
    initSupplyList();
  }, [
    locationInfo?.isInited,
  ]);

  // 请求供给列表数据：切换 tab 或快筛
  useEffect(() => {
    // 跳过初始化的调用，只响应后续的状态更新
    if (!isSkipInitRef.current) {
      isSkipInitRef.current = true;
      return;
    }
    getSupplyListData(true);
  }, [
    activeTab?.id ?? null,
    activeQuickSelection?.id ?? null,
    activeCouponSelection?.id ?? null,
  ]);

  // 检测导航栏是否吸顶
  useEffect(() => {
    if (!stickyDetectorRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // 关键判断：
        // - intersectionRatio === 0：哨兵完全不可见（滚出视口）
        // - boundingClientRect.top < 0：确保是向上滚动导致的不可见
        const isPinned = entry.intersectionRatio === 0 && entry.boundingClientRect.top < 0;
        if (isPinned) {
          setIsTabbarSticky(true);
        } else {
          setIsTabbarSticky(false);
        }
      },
      {
        threshold: [0.01]
      }
    );

    observer.observe(stickyDetectorRef.current);

    return () => {
      if (stickyDetectorRef.current) {
        observer.unobserve(stickyDetectorRef.current);
        observer.disconnect();
      }
    };
  }, []);

  // 点击红包墙去使用按钮，全局 couponRecallInfo 更新
  useEffect(() => {
    if (couponRecallInfo?.couponSupply?.wayneActId) {
      // Tabbar 吸顶
      handleTabSticky();
      // 更新券快筛
      handleCouponGoToUseClick(couponRecallInfo);
    }
  }, [couponRecallInfo, handleTabSticky, handleCouponGoToUseClick]);

  return (
    <>
      {/* 吸顶时导航栏的背景 */}
      {isTabbarSticky && <div className={`supply-area-sticky-bg ${appEnv.isMT ? 'no-navigation-bar' : ''} ${appEnv.isGrowthContainer && appEnv.isIos ? 'is-growth-container' : ''}`}></div>}
      <div className={`supply-area ${isTabbarSticky ? 'is-sticky' : ''} ${appEnv.isGrowthContainer && appEnv.isIos ? 'is-growth-container' : ''}`}>
        {/* 检测导航栏已吸顶，触发吸顶样式 */}
        <div ref={stickyDetectorRef} className={`stickty-detector ${appEnv.isMT ? 'no-navigation-bar' : ''}`}></div>
        {/* 需要吸顶的区域 */}
        <div ref={tabBarRef} className={`supply-navigation-wrapper ${appEnv.isMT ? 'no-navigation-bar' : ''}`}>
          {/* 导航栏 */}
          <Tabbar isInitFinished={isTabbarInitFinished} tabList={tabList} activeTab={activeTab} onTabClick={handleTabClickSticky} />
          {/* 快筛 */}
          <QuickSelectBar
            isInitFinished={isTabbarInitFinished}
            // 只有第一个 Tab 展示券快筛
            canShowCouponSelection={!activeTab || !activeTab.id}
            useCouponSelection={useCouponSelection}
            couponSelectionList={couponSelectionList}
            quickSelectionList={quickSelectionList}
            activeQuickSelection={activeQuickSelection}
            activeCouponSelection={activeCouponSelection}
            // 券快筛图标
            couponIconUrl={COUPON_SELECTION_ICON}
            lxConfig={LX_CONFIG}
            onQuickSelectionClick={handleQuickSelectionClickSticky}
            onCouponSelectionClick={handleCouponSelectionClickSticky as (selection: QuickSelection) => void}
          />
        </div>
        {/* 商品列表 */}
        <div className="supply-list-wrapper">
          <SupplyList
            supplyList={supplyList}
            isInitFinished={isSupplyListInitFinished}
            isLoading={isLoading}
            isLastPage={isLastPage}
            isRequestError={isRequestError}
            isFiltered={Boolean(activeQuickSelection || activeCouponSelection)} // 是否有筛选条件，用于判断兜底文案展示
            isCouponCallback={!!activeCouponSelection} // 是否券召回，埋点上报用
            isCouponPlatformStrategyRecall={true} // 是否券平台召回最优券策略
            enablePoiCouponCountdown={false}
            lxConfig={LX_CONFIG}
            tabId={activeTab?.id}
            onLoadMore={loadMoreData}
            onReload={() => getSupplyListData(true)}
          />
        </div>
      </div>
    </>
  );
};

export default SupplyArea;
